// /confirmar-correo/:token — the landing page for the email-change
// confirmation link (sent to the NEW address, per email.ts route). The user
// may click it logged out, so this page carries no auth. Auto-submits the
// token on mount, then shows 'done' or 'expired'. Shares the AuthLayout
// chrome with Login / ResetPassword; the tiny helpers below are copied local
// like ResetPassword does, not shared.

import { isAxiosError } from 'axios'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AuthLayout } from './AuthLayout.js'
import { confirmEmailChange } from './confirm-email-data.js'
import { Button, Spinner } from '../../design-system/index.js'

type View = 'working' | 'done' | 'expired'

function BackToLogin(): JSX.Element {
  return (
    <p className="mt-5 text-center">
      <Link
        to="/login"
        className="inline-flex items-center gap-1.5 rounded text-[12px] font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="size-3"
        >
          <path d="m15 6-6 6 6 6" />
        </svg>
        Volver a iniciar sesión
      </Link>
    </p>
  )
}

function StateBlock({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'ok' | 'warn'
  icon: ReactNode
  title: string
  children: ReactNode
}): JSX.Element {
  const toneClass = tone === 'ok' ? 'bg-ok-bg text-ok' : 'bg-warn-bg text-warn'
  return (
    <div className="text-center">
      <span
        aria-hidden
        className={`mx-auto grid size-[60px] place-items-center rounded-2xl ${toneClass}`}
      >
        {icon}
      </span>
      <h1 className="mt-4 text-[22px] font-bold tracking-[-0.022em] text-text">{title}</h1>
      {children}
    </div>
  )
}

const CHECK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="size-[30px]"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const CLOCK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="size-[28px]"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
)

const DEFAULT_EXPIRED_MESSAGE =
  'El enlace ya no es válido o expiró. Solicita el cambio de correo de nuevo desde tu cuenta.'

export function ConfirmEmailPage(): JSX.Element {
  const { token } = useParams()
  const navigate = useNavigate()
  const [view, setView] = useState<View>('working')
  const [kind, setKind] = useState<'primary' | 'secondary'>('primary')
  const [errorMessage, setErrorMessage] = useState(DEFAULT_EXPIRED_MESSAGE)
  const submitted = useRef(false)

  useEffect(() => {
    if (submitted.current) return
    submitted.current = true

    async function run(): Promise<void> {
      try {
        const result = await confirmEmailChange(token ?? '')
        setKind(result.kind)
        setView('done')
      } catch (err) {
        const msg =
          isAxiosError(err) &&
          typeof (err.response?.data as { error?: { message?: string } } | undefined)?.error
            ?.message === 'string'
            ? (err.response?.data as { error: { message: string } }).error.message
            : DEFAULT_EXPIRED_MESSAGE
        setErrorMessage(msg)
        setView('expired')
      }
    }
    void run()
  }, [token])

  return (
    <AuthLayout>
      {view === 'working' && (
        <div className="flex flex-col items-center gap-3 py-6">
          <Spinner label="Confirmando tu correo…" />
        </div>
      )}

      {view === 'done' && (
        <StateBlock
          tone="ok"
          icon={CHECK_ICON}
          title={kind === 'secondary' ? 'Tu correo secundario fue confirmado.' : 'Tu correo cambió'}
        >
          <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
            {kind === 'secondary'
              ? 'Ya podemos usarlo para enviarte notificaciones.'
              : 'Inicia sesión con tu nuevo correo.'}
          </p>
          <div className="mt-5">
            <Button block onClick={() => void navigate('/login')}>
              Ir a iniciar sesión
            </Button>
          </div>
        </StateBlock>
      )}

      {view === 'expired' && (
        <>
          <StateBlock tone="warn" icon={CLOCK_ICON} title="El enlace ya no sirve">
            <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
              {errorMessage}
            </p>
            <div className="mt-5">
              <Button block onClick={() => void navigate('/login')}>
                Ir a iniciar sesión
              </Button>
            </div>
          </StateBlock>
          <BackToLogin />
        </>
      )}
    </AuthLayout>
  )
}
